// ── AdBoost Dashboard Card ──
// Add this component inside Dashboard.js, after the fetchTopCustomers function
// Then add <AdBoostCard sellerId={seller?.id} navigate={navigate} /> 
// between the top customers card and recent activity card in the JSX

import { useState, useEffect } from 'react'
import { supabase } from '../supabase'

const BLUE   = '#1877F2'
const TEXT   = '#050505'
const MUTED  = '#65676B'
const BORDER = '#E4E6EB'
const BG     = '#F0F2F5'
const WHITE  = '#FFFFFF'
const GREEN  = '#16A34A'
const AMBER  = '#D97706'
const RED    = '#DC2626'

function AdBoostCard({ sellerId, navigate }) {
  const [boosts, setBoosts] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!sellerId) return
    const fetch = async () => {
      const { data } = await supabase
        .from('boosts')
        .select('id, boost_number, fb_campaign_name, status, goal, budget_bdt, duration_days, launched_at, ends_at, result_reach, result_impressions, result_clicks')
        .eq('seller_id', sellerId)
        .order('created_at', { ascending: false })
        .limit(5)
      setBoosts(data || [])
      setLoading(false)
    }
    fetch()
  }, [sellerId])

  const active    = boosts.filter(b => b.status === 'running')
  const pending   = boosts.filter(b => ['pending','paid','preparing'].includes(b.status))
  const lastDone  = boosts.find(b => b.status === 'completed')
  const hasBoosts = boosts.length > 0

  const getDaysLeft = (ends_at) => {
    if (!ends_at) return null
    const diff = new Date(ends_at).getTime() - Date.now()
    return Math.ceil(diff / 86400000)
  }

  const getProgress = (launched_at, ends_at) => {
    if (!launched_at || !ends_at) return 0
    const start = new Date(launched_at).getTime()
    const end   = new Date(ends_at).getTime()
    return Math.min(100, Math.max(0, ((Date.now() - start) / (end - start)) * 100))
  }

  if (loading) return null

  return (
    <div style={{ background: WHITE, borderRadius: '12px', border: '1px solid ' + BORDER, overflow: 'hidden', flexShrink: 0 }}>
      {/* Header */}
      <div style={{ padding: '13px 16px', borderBottom: '1px solid ' + BORDER, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div>
          <div style={{ fontSize: '13px', fontWeight: 700, color: TEXT }}>📢 AdBoost</div>
          <div style={{ fontSize: '11.5px', color: MUTED, marginTop: '2px' }}>
            {active.length > 0 ? `${active.length} campaign${active.length > 1 ? 's' : ''} running` : 'Boost your Facebook reach'}
          </div>
        </div>
        <button onClick={() => navigate('/grow/adboost')}
          style={{ fontSize: '12px', fontWeight: 600, color: BLUE, background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'Space Grotesk, sans-serif' }}>
          {hasBoosts ? 'View all' : 'Get started'}
        </button>
      </div>

      {!hasBoosts ? (
        /* No campaigns yet — CTA */
        <div style={{ padding: '20px 16px', textAlign: 'center' }}>
          <div style={{ fontSize: '28px', marginBottom: '8px' }}>🚀</div>
          <div style={{ fontSize: '12.5px', fontWeight: 700, color: TEXT, marginBottom: '4px' }}>Grow your sales with AdBoost</div>
          <div style={{ fontSize: '11.5px', color: MUTED, marginBottom: '14px', lineHeight: 1.5 }}>
            Boost your Facebook posts and page — we handle everything. Pay in bKash/Nagad.
          </div>
          <button onClick={() => navigate('/grow/adboost')}
            style={{ padding: '8px 20px', borderRadius: '9px', background: BLUE, border: 'none', fontSize: '12.5px', fontWeight: 700, color: WHITE, cursor: 'pointer', fontFamily: 'Space Grotesk, sans-serif' }}>
            Create First Boost
          </button>
        </div>
      ) : (
        <div>
          {/* Active campaigns */}
          {active.map(b => {
            const daysLeft = getDaysLeft(b.ends_at)
            const pct      = getProgress(b.launched_at, b.ends_at)
            const isEnding = daysLeft !== null && daysLeft <= 1
            return (
              <div key={b.id} onClick={() => navigate('/grow/adboost')}
                style={{ padding: '11px 16px', borderBottom: '1px solid #F5F5F5', cursor: 'pointer' }}
                onMouseEnter={e => e.currentTarget.style.background = BG}
                onMouseLeave={e => e.currentTarget.style.background = WHITE}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '6px' }}>
                  <div style={{ fontSize: '12px', fontWeight: 600, color: TEXT, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, marginRight: '8px' }}>
                    {b.fb_campaign_name || `Campaign #${b.boost_number}`}
                  </div>
                  <span style={{ fontSize: '10.5px', fontWeight: 700, color: isEnding ? RED : GREEN, background: isEnding ? '#FEF2F2' : '#F0FDF4', padding: '2px 8px', borderRadius: '20px', flexShrink: 0 }}>
                    {isEnding ? '🔴 Ending today' : `🟢 Day ${Math.ceil(pct * b.duration_days / 100)} of ${b.duration_days}`}
                  </span>
                </div>
                <div style={{ height: '4px', background: BORDER, borderRadius: '99px', overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: pct + '%', background: isEnding ? RED : BLUE, borderRadius: '99px' }} />
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '4px' }}>
                  <span style={{ fontSize: '10.5px', color: MUTED }}>৳{b.budget_bdt?.toLocaleString()} · {b.goal?.replace(/_/g, ' ')}</span>
                  <span style={{ fontSize: '10.5px', color: isEnding ? RED : MUTED, fontWeight: isEnding ? 700 : 400 }}>
                    {daysLeft !== null ? (daysLeft <= 0 ? 'Ends today' : `${daysLeft}d left`) : ''}
                  </span>
                </div>
              </div>
            )
          })}

          {/* Pending */}
          {pending.length > 0 && (
            <div onClick={() => navigate('/grow/adboost')}
              style={{ padding: '10px 16px', borderBottom: '1px solid #F5F5F5', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
              onMouseEnter={e => e.currentTarget.style.background = BG}
              onMouseLeave={e => e.currentTarget.style.background = WHITE}>
              <span style={{ fontSize: '12px', color: MUTED }}>{pending.length} campaign{pending.length > 1 ? 's' : ''} being prepared</span>
              <span style={{ fontSize: '10.5px', fontWeight: 700, color: AMBER, background: '#FFFBEB', padding: '2px 8px', borderRadius: '20px' }}>🟡 Pending</span>
            </div>
          )}

          {/* Last completed */}
          {lastDone && lastDone.result_reach && (
            <div onClick={() => navigate('/grow/adboost')}
              style={{ padding: '10px 16px', borderBottom: '1px solid #F5F5F5', cursor: 'pointer' }}
              onMouseEnter={e => e.currentTarget.style.background = BG}
              onMouseLeave={e => e.currentTarget.style.background = WHITE}>
              <div style={{ fontSize: '11px', fontWeight: 700, color: MUTED, marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Last Campaign Results</div>
              <div style={{ display: 'flex', gap: '12px' }}>
                {[
                  { label: 'Reach', val: lastDone.result_reach?.toLocaleString() },
                  { label: 'Clicks', val: lastDone.result_clicks?.toLocaleString() },
                  { label: 'Impressions', val: lastDone.result_impressions?.toLocaleString() },
                ].filter(r => r.val).map(r => (
                  <div key={r.label}>
                    <div style={{ fontSize: '13px', fontWeight: 800, color: BLUE }}>{r.val}</div>
                    <div style={{ fontSize: '10px', color: MUTED }}>{r.label}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* New boost CTA */}
          <div style={{ padding: '10px 16px' }}>
            <button onClick={() => navigate('/grow/adboost')}
              style={{ width: '100%', padding: '8px', borderRadius: '9px', background: '#EFF6FF', border: '1px solid #BFDBFE', fontSize: '12.5px', fontWeight: 700, color: BLUE, cursor: 'pointer', fontFamily: 'Space Grotesk, sans-serif' }}>
              + New Boost
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

export default AdBoostCard